import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  PackagePlus,
  ListChecks,
  Package,
  Hourglass,
  ArrowRight,
  TableProperties,
  Megaphone,
  CheckCheck,
  Warehouse,
  PlayCircle,
  Inbox,
  Handshake,
  AlertTriangle,
  Timer,
  Boxes,
  AlertOctagon,
  Bolt,
  Wrench,
  FileText,
} from "lucide-react";
import { StatusPill } from "../components/StatusPill";
import { PriorityBadge, EtaBadge } from "../components/Badges";
import { formatLocalDateTime } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { isRunnerLevel, isViewer, role } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    in_yard: 0,
    missing_storage: 0,
    pending_preparation: 0,
    preparing: 0,
    ready_for_handover: 0,
    completed_today: 0,
    ready_for_handover_for_me: 0,
    urgent_items: 0,
    avg_queue_time_min: 0,
    low_stock_count: 0,
  });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/shipments/stats"),
          api.get("/shipments", { params: { limit: 8 } }),
        ]);
        setStats(s.data);
        setRecent(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-10">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
          Yard Operations
        </div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase text-slate-900 tracking-tight">
          Dashboard
        </h1>
      </div>

      {/* Hero RECEIVE button — most prominent yard action (visible to all authenticated users) */}
      <Link
        to="/shipments/receive"
        data-testid="dashboard-hero-receive-btn"
        className="group mb-6 p-5 sm:p-6 bg-green-700 hover:bg-green-800 border-2 border-green-900 hover:border-green-950 rounded-md text-white flex items-center gap-4 sm:gap-5 transition-colors"
      >
        <div className="h-14 w-14 sm:h-16 sm:w-16 grid place-items-center bg-white text-green-700 rounded-sm flex-shrink-0">
          <PackagePlus strokeWidth={3} className="h-9 w-9 sm:h-10 sm:w-10" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-green-200">
            Step 1 — Yard intake
          </div>
          <div className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none mt-1 text-white">
            Receive
          </div>
          <div className="text-xs sm:text-sm text-green-100 mt-1">
            Scan or type the MR. Quick mode finishes a box in under 20 seconds.
          </div>
        </div>
        <ArrowRight strokeWidth={3} className="h-7 w-7 sm:h-8 sm:w-8 text-white opacity-90 group-hover:opacity-100 flex-shrink-0" />
      </Link>

      {/* Equipment + Tool Request — secondary hero pair (highly visible) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
        <Link
          to="/equipment"
          data-testid="dashboard-equipment-btn"
          className="group p-4 sm:p-5 bg-slate-900 hover:bg-slate-800 border-2 border-slate-950 rounded-md text-white flex items-center gap-3 sm:gap-4 transition-colors"
        >
          <div className="h-12 w-12 sm:h-14 sm:w-14 grid place-items-center bg-amber-500 text-slate-900 rounded-sm flex-shrink-0">
            <Wrench strokeWidth={2.5} className="h-7 w-7 sm:h-8 sm:w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300">
              Yard equipment
            </div>
            <div className="font-display text-xl sm:text-2xl uppercase tracking-tight leading-none mt-0.5 text-white">
              Equipment
            </div>
            <div className="text-[11px] sm:text-xs text-slate-300 mt-1">
              Checkouts, calibrations &amp; tool inventory.
            </div>
          </div>
          <ArrowRight strokeWidth={2.5} className="h-5 w-5 sm:h-6 sm:w-6 text-white opacity-80 group-hover:opacity-100 flex-shrink-0" />
        </Link>
        <Link
          to="/tool-requests/new"
          data-testid="dashboard-tool-request-btn"
          className="group p-4 sm:p-5 bg-emerald-700 hover:bg-emerald-800 border-2 border-emerald-900 rounded-md text-white flex items-center gap-3 sm:gap-4 transition-colors"
        >
          <div className="h-12 w-12 sm:h-14 sm:w-14 grid place-items-center bg-white text-emerald-700 rounded-sm flex-shrink-0">
            <FileText strokeWidth={2.5} className="h-7 w-7 sm:h-8 sm:w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-200">
              ComEd approval letter
            </div>
            <div className="font-display text-xl sm:text-2xl uppercase tracking-tight leading-none mt-0.5 text-white">
              Tool Request
            </div>
            <div className="text-[11px] sm:text-xs text-emerald-100 mt-1">
              Pick approved tools &amp; send a letter to supervisor.
            </div>
          </div>
          <ArrowRight strokeWidth={2.5} className="h-5 w-5 sm:h-6 sm:w-6 text-white opacity-80 group-hover:opacity-100 flex-shrink-0" />
        </Link>
      </div>

      {/* Action tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
        {!isViewer && (
        <ActionTile
          to="/requests/new"
          testid="dashboard-request-btn"
          icon={Megaphone}
          title="Submit Crew Request"
          subtitle="Crew leader request to Yard Runner."
          theme="purple"
        />
        )}
        <ActionTile
          to="/preparation-queue"
          testid="dashboard-runner-queue-btn"
          icon={ListChecks}
          title="Yard Runner Queue"
          subtitle="Pending + In Progress + Handover."
          theme="amber"
          badge={stats.pending_preparation + stats.preparing}
        />
        <ActionTile
          to="/my-requests"
          testid="dashboard-my-requests-btn"
          icon={Bolt}
          title="My Requests"
          subtitle="Requests you've submitted."
          theme="dark"
        />
        <ActionTile
          to="/my-pickups"
          testid="dashboard-my-pickups-btn"
          icon={Inbox}
          title="My Pending Pickups"
          subtitle="Ready for Handover — go pick it up."
          theme="indigo"
          badge={stats.ready_for_handover_for_me}
        />
        <ActionTile
          to="/inventory"
          testid="dashboard-low-inventory-btn"
          icon={Boxes}
          title="Low Inventory"
          subtitle="Items at or below threshold."
          theme="red"
          badge={stats.low_stock_count}
        />
        <ActionTile
          to="/completed-tasks"
          testid="dashboard-completed-btn"
          icon={CheckCheck}
          title="Completed Tasks"
          subtitle="Recently completed by the runner."
          theme="dark"
          badge={stats.completed_today}
        />
      </div>

      {/* Smart widgets — 7 per spec, all fully clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4 mb-8">
        <StatCard
          label="Pending Preparation"
          value={stats.pending_preparation}
          icon={Hourglass}
          testid="stat-pending-prep"
          accent="border-amber-700"
          to="/preparation-queue?tab=pending"
        />
        <StatCard
          label="Preparing Now"
          value={stats.preparing}
          icon={PlayCircle}
          testid="stat-preparing"
          accent="border-blue-700"
          to="/preparation-queue?tab=in_progress"
        />
        <StatCard
          label="Ready for Handover"
          value={stats.ready_for_handover}
          icon={Handshake}
          testid="stat-handover-total"
          accent="border-purple-700"
          to="/preparation-queue?tab=handover"
        />
        <StatCard
          label="My Ready Items"
          value={stats.ready_for_handover_for_me}
          icon={Inbox}
          testid="stat-handover-mine"
          accent="border-indigo-700"
          to="/my-pickups"
        />
        <StatCard
          label="Urgent Items"
          value={stats.urgent_items}
          icon={AlertTriangle}
          testid="stat-urgent"
          accent="border-rose-700"
          danger={stats.urgent_items > 0}
          to="/preparation-queue?priority=Urgent&tab=pending"
        />
        <StatCard
          label="Low Stock Alerts"
          value={stats.low_stock_count}
          icon={AlertOctagon}
          testid="stat-low-stock"
          accent="border-rose-700"
          danger={stats.low_stock_count > 0}
          to="/inventory"
        />
        <StatCard
          label="Completed Today"
          value={stats.completed_today}
          icon={CheckCheck}
          testid="stat-completed-today"
          accent="border-emerald-700"
          to="/completed-tasks?range=today"
        />
      </div>

      {/* Yard Cleanup banner — shows only when there are legacy yard rows missing Stocking Location */}
      {stats.missing_storage > 0 && (
        <Link
          to="/shipments?missing_storage=1"
          data-testid="dashboard-yard-cleanup-tile"
          className="group mb-8 flex items-center gap-4 sm:gap-5 p-4 sm:p-5 bg-amber-50 hover:bg-amber-100 border-2 border-amber-400 hover:border-amber-600 rounded-md transition-colors"
        >
          <div className="h-12 w-12 sm:h-14 sm:w-14 grid place-items-center bg-amber-500 text-slate-900 rounded-sm flex-shrink-0 border-2 border-amber-700">
            <Warehouse strokeWidth={2.5} className="h-7 w-7 sm:h-8 sm:w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
              Housekeeping · Yard Cleanup
            </div>
            <div className="font-display text-xl sm:text-2xl uppercase tracking-tight leading-tight mt-0.5 text-amber-900">
              <span data-testid="yard-cleanup-count">{stats.missing_storage}</span>
              {" "}
              shipment{stats.missing_storage === 1 ? "" : "s"} need a stocking spot
            </div>
            <div className="text-xs sm:text-sm text-amber-900/80 mt-1">
              Knock these out in one sweep — tap the row to set a Bay/Rack/Shelf.
            </div>
          </div>
          <ArrowRight strokeWidth={3} className="h-6 w-6 sm:h-7 sm:w-7 text-amber-800 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
        </Link>
      )}

      {/* Recent shipments */}
      <div className="rg-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg uppercase text-slate-900">Recent shipments</h2>
          <Link
            to="/shipments"
            data-testid="view-all-shipments-link"
            className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-700 hover:text-slate-900"
          >
            <TableProperties className="h-4 w-4" />
            View all
          </Link>
        </div>
        {loading ? (
          <div className="text-slate-500 text-sm">Loading…</div>
        ) : recent.length === 0 ? (
          <div className="border-2 border-dashed border-slate-300 p-8 text-center text-slate-500 rounded-sm">
            No shipments logged yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b-2 border-slate-200">
                  <th className="py-2 pr-4">MR</th>
                  <th className="py-2 pr-4 hidden sm:table-cell">Priority</th>
                  <th className="py-2 pr-4">Station</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 hidden md:table-cell">ETA</th>
                  <th className="py-2 pr-4 hidden md:table-cell">Updated</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-100"
                    data-testid={`recent-row-${s.MR_Number}`}
                  >
                    <td className="py-2 pr-4">
                      <Link
                        to={`/shipments/${s.id}`}
                        className="font-mono font-bold text-slate-900 hover:underline"
                      >
                        {s.MR_Number}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 hidden sm:table-cell">
                      {s.Priority && s.Priority !== "Normal" ? <PriorityBadge value={s.Priority} /> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 pr-4 text-slate-700">{s.Project_Station}</td>
                    <td className="py-2 pr-4">
                      <StatusPill value={s.Status} kind="status" />
                    </td>
                    <td className="py-2 pr-4 hidden md:table-cell">
                      {s.Estimated_Ready_Time ? (
                        <EtaBadge etaText={s.Estimated_Ready_Time} etaIso={s.Estimated_Ready_Iso} />
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-slate-500 hidden md:table-cell">
                      {formatLocalDateTime(s.Last_Updated)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const TILE_THEMES = {
  dark: "bg-white hover:bg-slate-900 hover:text-white border-slate-300 hover:border-slate-900 [&_.tile-icon]:bg-slate-900 [&_.tile-icon]:text-white hover:[&_.tile-icon]:bg-amber-500 hover:[&_.tile-icon]:text-slate-900",
  purple:
    "bg-white hover:bg-purple-700 hover:text-white border-purple-300 hover:border-purple-900 [&_.tile-icon]:bg-purple-700 [&_.tile-icon]:text-white hover:[&_.tile-icon]:bg-white hover:[&_.tile-icon]:text-purple-700",
  amber:
    "bg-white hover:bg-amber-500 hover:text-slate-900 border-amber-300 hover:border-amber-700 [&_.tile-icon]:bg-amber-500 [&_.tile-icon]:text-slate-900",
  green:
    "bg-white hover:bg-green-700 hover:text-white border-green-300 hover:border-green-900 [&_.tile-icon]:bg-green-700 [&_.tile-icon]:text-white hover:[&_.tile-icon]:bg-white hover:[&_.tile-icon]:text-green-700",
  indigo:
    "bg-white hover:bg-indigo-700 hover:text-white border-indigo-300 hover:border-indigo-900 [&_.tile-icon]:bg-indigo-700 [&_.tile-icon]:text-white hover:[&_.tile-icon]:bg-white hover:[&_.tile-icon]:text-indigo-700",
  red:
    "bg-white hover:bg-rose-700 hover:text-white border-rose-300 hover:border-rose-900 [&_.tile-icon]:bg-rose-700 [&_.tile-icon]:text-white hover:[&_.tile-icon]:bg-white hover:[&_.tile-icon]:text-rose-700",
};

function ActionTile({ to, testid, icon: Icon, title, subtitle, theme, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group rg-card p-5 transition-colors flex items-center gap-3 min-h-[110px] ${TILE_THEMES[theme]}`}
    >
      <div className="tile-icon h-12 w-12 grid place-items-center rounded-sm relative flex-shrink-0">
        <Icon strokeWidth={2.5} className="h-7 w-7" />
        {badge !== undefined && badge > 0 && (
          <span
            className="absolute -top-2 -right-2 h-6 min-w-6 px-1.5 grid place-items-center bg-amber-500 text-slate-900 font-display text-xs rounded-sm border-2 border-white"
            data-testid={`${testid}-badge`}
          >
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base sm:text-lg uppercase tracking-tight leading-tight">
          {title}
        </div>
        <div className="text-xs sm:text-sm opacity-80 mt-1">{subtitle}</div>
      </div>
      <ArrowRight strokeWidth={3} className="h-5 w-5 opacity-50 group-hover:opacity-100 flex-shrink-0" />
    </Link>
  );
}

function formatMinutes(m) {
  if (!m || m < 1) return "—";
  if (m < 60) return `${m}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(h < 10 ? 1 : 0)}h`;
  const d = h / 24;
  return `${d.toFixed(d < 10 ? 1 : 0)}d`;
}

function StatCard({ label, value, icon: Icon, testid, accent, danger, to }) {
  const content = (
    <>
      <div className="flex items-start justify-between mb-2">
        <Icon strokeWidth={2.5} className={`h-5 w-5 ${danger ? "text-rose-700" : "text-slate-700"}`} />
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1">
          {to && (
            <ArrowRight
              strokeWidth={3}
              className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-arrow`}
            />
          )}
          Live
        </div>
      </div>
      <div
        className={`font-display text-3xl sm:text-4xl leading-none mb-1 ${danger ? "text-rose-700" : "text-slate-900"}`}
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      <div className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600">
        {label}
      </div>
    </>
  );

  // Clickable variant — Link with hover lift + amber border highlight
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        aria-label={`${label}: ${value}. Click to open filtered view.`}
        className={`group rg-card p-4 sm:p-5 ${accent} border-l-[6px] block ${
          danger ? "bg-rose-50/40" : ""
        } transition-all hover:border-l-amber-500 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1`}
      >
        {content}
      </Link>
    );
  }

  // Static variant
  return (
    <div
      className={`rg-card p-4 sm:p-5 ${accent} border-l-[6px] ${danger ? "bg-rose-50/40" : ""}`}
      data-testid={testid}
    >
      {content}
    </div>
  );
}
